import {
  BODY_OPACITY,
  CANVAS_BLEED,
  DEPTH_EDGE_ALPHA,
  DEPTH_LIGHT_X_RATIO,
  DEPTH_LIGHT_Y_RATIO,
  FLUID_GRID_MAX,
  FLUID_GRID_MIN,
  FLUID_GRID_RATIO,
  FLUID_REPAINT_FPS,
  FLUID_TIME_SCALE,
  HALO_LEVEL_SWELL,
  HALO_REACH_RATIO,
  LEVEL_ATTACK_TAU,
  LEVEL_RELEASE_TAU,
  RING_RADIUS_RATIO,
  RING_WIDTH_RATIO,
  SETTLE_EPSILON,
  STATE_FEEL,
  STATE_MOTION,
  STATE_TAU,
  TEXTURE_OFFSET_X_RATIO,
  TEXTURE_OFFSET_Y_RATIO,
  TEXTURE_SCALE,
  type StateFeel,
} from "./constants";
import { createFluid, type Fluid } from "./fluid";
import { approach, clamp, clamp01, easeInOutSine, wrap } from "./math";
import {
  mixRgb,
  PALETTE_FALLBACK,
  readPalette,
  rgba,
  type OrbPalette,
} from "./palette";
import type { WakeOrbState } from "./types";

const TAU = Math.PI * 2;

/** Longest frame we integrate. Past this we assume a tab stall, not slow motion. */
const MAX_DT = 0.05;

const FEEL_KEYS = [
  "body",
  "ring",
  "texture",
  "depth",
  "fluid",
  "fluidBright",
  "fluidFlow",
  "fluidGamma",
  "fluidSpeed",
  "halo",
  "haloPulse",
  "haloSuccess",
  "rateHz",
  "breathe",
  "levelGain",
] as const;

export type OrbLayers = {
  /** The speaking orb's cloud texture, Figma `20168:41427`. */
  texture: HTMLImageElement | null;
};

export type OrbRenderer = {
  /** `orbCssSize` is the *designed* box; the canvas itself bleeds past it. */
  resize(orbCssSize: number, dpr: number): void;
  setState(state: WakeOrbState): void;
  setLevel(level: number): void;
  setLayer(layer: keyof OrbLayers, image: HTMLImageElement): void;
  /** Suppresses looping motion; cross-fades and level response still work. */
  setReducedMotion(reduced: boolean): void;
  /**
   * Paints one frame. Returns `true` while more frames are needed — either the
   * state animates continuously, or values are still easing toward it. The
   * caller uses this to stop the loop instead of spinning on a still image.
   */
  frame(nowMs: number): boolean;
  dispose(): void;
};

/**
 * Canvas painter for the orb. Deliberately framework-free and mutable: React
 * pushes props in through the setters, so a prop change never restarts the
 * animation loop or resets the eased visual state.
 *
 * Two animations run independently, back to front:
 *   halo (glows) -> disc -> texture -> interior fluid (flows) -> depth -> ring.
 * The mic glyph is DOM, above all. Nothing rotates.
 */
export function createOrbRenderer(canvas: HTMLCanvasElement): OrbRenderer {
  const ctx = canvas.getContext("2d");

  let palette: OrbPalette = PALETTE_FALLBACK;
  const layers: OrbLayers = { texture: null };
  let reducedMotion = false;

  let fluid: Fluid | null = null;
  let fluidGrid = 0;
  let lastFluidMs = 0;

  /** Backing-store size of the whole canvas, including bleed. */
  let canvasPx = 0;
  /** Backing-store size of the designed orb box. All ratios key off this. */
  let orbPx = 0;

  let targetState: WakeOrbState = "idle";
  let target: StateFeel = STATE_FEEL.idle;
  const visual: StateFeel = { ...STATE_FEEL.idle };
  let primed = false;

  let targetLevel = 0;
  let level = 0;

  let phase = 0;
  /** Integrated flow clock. Never `t * speed` — that teleports the texture. */
  let flowTime = 0;
  let lastMs = 0;

  function resize(orbCssSize: number, dpr: number) {
    orbPx = Math.max(1, orbCssSize * dpr);
    canvasPx = Math.max(1, Math.round(orbCssSize * CANVAS_BLEED * dpr));

    // The fluid buffer is sized to the orb, not the backing store — it gets
    // upscaled with smoothing, and the field is soft enough that a ~3x
    // upscale is indistinguishable from painting at full resolution.
    const grid = clamp(
      Math.round(orbCssSize * FLUID_GRID_RATIO),
      FLUID_GRID_MIN,
      FLUID_GRID_MAX,
    );
    if (grid !== fluidGrid) {
      fluidGrid = grid;
      fluid = createFluid(grid);
      lastFluidMs = 0;
    }

    // Assigning width/height also clears the canvas and resets context state,
    // which is fine — every style is set fresh each frame.
    canvas.width = canvasPx;
    canvas.height = canvasPx;
    // Tokens can only change with a stylesheet or theme swap, both of which
    // trigger a resize far more often than they don't. Never read per frame.
    palette = readPalette(canvas);
  }

  function setState(state: WakeOrbState) {
    targetState = state;
    target = STATE_FEEL[state];
    // Fluid-low (and friends) are overridden per state in CSS. Re-read here —
    // resize alone misses class changes, and a stale `fluidLow` of the dark
    // disc colour shows up as black clouds inside dormant/speaking.
    palette = readPalette(canvas);
    lastFluidMs = 0;
  }

  function setLevel(next: number) {
    targetLevel = clamp01(next);
  }

  function setLayer(layer: keyof OrbLayers, image: HTMLImageElement) {
    layers[layer] = image;
  }

  function setReducedMotion(reduced: boolean) {
    reducedMotion = reduced;
  }

  function advance(nowMs: number) {
    const dt = lastMs === 0 ? 1 / 60 : clamp((nowMs - lastMs) / 1000, 0, MAX_DT);
    lastMs = nowMs;

    if (primed) {
      for (const key of FEEL_KEYS) {
        visual[key] = approach(visual[key], target[key], dt, STATE_TAU);
      }
    } else {
      // First frame lands on the target rather than fading up from idle.
      for (const key of FEEL_KEYS) visual[key] = target[key];
      primed = true;
    }

    // Fast attack / slow release, so a loud syllable spikes and then decays
    // instead of the orb chattering with every RMS sample.
    const tau = targetLevel > level ? LEVEL_ATTACK_TAU : LEVEL_RELEASE_TAU;
    level = approach(level, targetLevel, dt, tau);

    if (!reducedMotion) {
      phase = wrap(phase + visual.rateHz * dt, 1);
      // Integrated, not `t * speed`: multiplying time by a changing speed
      // retroactively rewrites history and makes the field jump.
      const speed = visual.fluidSpeed * (1 + visual.levelGain * level * 1.2);
      flowTime += dt * speed * FLUID_TIME_SCALE;
    }
  }

  function repaintFluid(nowMs: number) {
    if (!fluid) return;
    // Throttled: the field drifts slowly, so 30fps here is invisible while
    // compositing continues at full rate.
    const due = nowMs - lastFluidMs >= 1000 / FLUID_REPAINT_FPS;
    if (lastFluidMs !== 0 && !due) return;
    lastFluidMs = nowMs;
    fluid.paint({
      time: flowTime,
      bright: clamp01(visual.fluidBright),
      flow: clamp01(visual.fluidFlow),
      // Floored: a gamma of 0 would flatten the whole field to solid white.
      gamma: Math.max(0.05, visual.fluidGamma),
      palette,
    });
  }

  function draw() {
    if (!ctx || canvasPx === 0) return;

    ctx.clearRect(0, 0, canvasPx, canvasPx);

    const c = canvasPx / 2;
    // easeInOutSine returns 0..1; recentre to -0.5..0.5 for symmetric swings.
    const swing = easeInOutSine(phase) - 0.5;
    const breatheScale = 1 + visual.breathe * swing * 2;
    const r = orbPx * RING_RADIUS_RATIO * breatheScale;
    const ringWidth = Math.max(1, orbPx * RING_WIDTH_RATIO);

    // --- 1. outer halo ----------------------------------------------------
    // This layer only ever *glows*: it pulses in reach and alpha and is driven
    // by audio level. It never rotates — all rotation happens inside the disc.
    const haloAlpha = clamp01(
      visual.halo * (1 + visual.haloPulse * swing * 1.4) +
        visual.levelGain * level * 0.3,
    );
    if (haloAlpha > 0.002) {
      const swell =
        1 +
        visual.haloPulse * swing * 0.5 +
        visual.levelGain * level * HALO_LEVEL_SWELL;
      const reach = orbPx * HALO_REACH_RATIO * Math.max(0.2, swell);
      const outer = r + reach;
      // Success turns the halo green; every other state leaves it teal.
      const glow =
        visual.haloSuccess > 0.002
          ? mixRgb(palette.glow, palette.success, clamp01(visual.haloSuccess))
          : palette.glow;
      // Start the fade just inside the rim so there is no visible seam.
      const grad = ctx.createRadialGradient(c, c, r * 0.9, c, c, outer);
      grad.addColorStop(0, rgba(glow, haloAlpha * 0.55));
      grad.addColorStop(0.35, rgba(glow, haloAlpha * 0.28));
      grad.addColorStop(0.7, rgba(glow, haloAlpha * 0.08));
      grad.addColorStop(1, rgba(glow, 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(c, c, outer, 0, TAU);
      ctx.fill();
    }

    // --- 2. translucent dark disc ----------------------------------------
    if (visual.body > 0.002) {
      ctx.fillStyle = rgba(palette.body, BODY_OPACITY * clamp01(visual.body));
      ctx.beginPath();
      ctx.arc(c, c, r, 0, TAU);
      ctx.fill();
    }

    // --- 3. speaking texture, clipped to the circle -----------------------
    if (layers.texture && visual.texture > 0.002) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(c, c, r, 0, TAU);
      ctx.clip();
      ctx.globalAlpha = clamp01(visual.texture);
      // Figma's image fill overhangs the ellipse, so the cloud is cropped by
      // the circle rather than fitted to it. Convert the designed box
      // (scale + top-left inset) into an offset from the orb's centre. Drawn
      // square-on: no rotation in any state.
      const box = orbPx * TEXTURE_SCALE;
      const dx = orbPx * (TEXTURE_OFFSET_X_RATIO + TEXTURE_SCALE / 2 - 0.5);
      const dy = orbPx * (TEXTURE_OFFSET_Y_RATIO + TEXTURE_SCALE / 2 - 0.5);
      ctx.drawImage(
        layers.texture,
        c + dx - box / 2,
        c + dy - box / 2,
        box,
        box,
      );
      ctx.restore();
    }

    // --- 4. interior fluid ------------------------------------------------
    // The animated core: a domain-warped noise field that flows in place.
    // Upscaled into the clip with smoothing, so the coarse buffer reads soft.
    if (fluid && visual.fluid > 0.002) {
      const box = r * 2 * breatheScale;
      ctx.save();
      ctx.beginPath();
      ctx.arc(c, c, r, 0, TAU);
      ctx.clip();
      ctx.globalAlpha = clamp01(visual.fluid);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(fluid.canvas, c - box / 2, c - box / 2, box, box);
      ctx.restore();
    }

    // --- 5. depth shading -------------------------------------------------
    // Off-centre darkening so the disc reads as a lit sphere rather than a flat
    // circle. Black only, so it shades whatever is underneath without
    // introducing a colour — but it does still darken, so states whose artwork
    // already has its own rim shading turn this down via `depth`.
    if (visual.depth > 0.004) {
      const edge = DEPTH_EDGE_ALPHA * clamp01(visual.depth);
      ctx.save();
      ctx.beginPath();
      ctx.arc(c, c, r, 0, TAU);
      ctx.clip();
      const lx = c + r * DEPTH_LIGHT_X_RATIO;
      const ly = c + r * DEPTH_LIGHT_Y_RATIO;
      const depth = ctx.createRadialGradient(lx, ly, r * 0.08, c, c, r);
      depth.addColorStop(0, "rgba(0, 0, 0, 0)");
      depth.addColorStop(0.6, `rgba(0, 0, 0, ${edge * 0.3})`);
      depth.addColorStop(1, `rgba(0, 0, 0, ${edge})`);
      ctx.fillStyle = depth;
      ctx.fillRect(c - r, c - r, r * 2, r * 2);
      ctx.restore();
    }

    // --- 6. ring ----------------------------------------------------------
    if (visual.ring > 0.004) {
      // Level lifts the ring, but only for states that listen to level at all.
      const ringAlpha = clamp01(
        visual.ring * (1 - 0.25 * clamp01(visual.levelGain) * (1 - level)),
      );
      ctx.lineWidth = ringWidth;
      ctx.strokeStyle = rgba(palette.ring, ringAlpha);
      ctx.beginPath();
      ctx.arc(c, c, r, 0, TAU);
      ctx.stroke();
    }
  }

  function settled() {
    for (const key of FEEL_KEYS) {
      if (Math.abs(visual[key] - target[key]) > SETTLE_EPSILON) return false;
    }
    // Level only counts if the current state actually renders it, otherwise a
    // live mic would keep the loop alive behind a state that ignores it.
    if (target.levelGain > 0 && Math.abs(level - targetLevel) > SETTLE_EPSILON) {
      return false;
    }
    return true;
  }

  function frame(nowMs: number): boolean {
    advance(nowMs);
    repaintFluid(nowMs);
    draw();
    return STATE_MOTION[targetState] || !settled();
  }

  function dispose() {
    layers.texture = null;
    fluid = null;
    fluidGrid = 0;
    lastMs = 0;
    lastFluidMs = 0;
    primed = false;
  }

  return {
    resize,
    setState,
    setLevel,
    setLayer,
    setReducedMotion,
    frame,
    dispose,
  };
}
